import type { LobbyInfoDTO } from "../../shared/types";
import { buildLogoSvg } from "./logo";

const CODE_PATTERN = /^[A-Z0-9]{8}$/;

function parseCodeFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/lobby\/([A-Za-z0-9]+)\/?$/);
  if (!match) return null;
  const code = match[1]!.toUpperCase();
  return CODE_PATTERN.test(code) ? code : null;
}

async function fetchLobbyInfo(code: string): Promise<LobbyInfoDTO> {
  const res = await fetch(`/api/lobbies/${code}`);
  return res.json();
}

/** Fails open (reports enabled) on any error - a status-check hiccup should
 * never be the reason someone can't see the "Create" button; the server's own
 * POST /api/lobbies is the real enforcement point regardless of what this
 * reports. */
async function fetchLobbyCreationEnabled(): Promise<boolean> {
  try {
    const res = await fetch("/api/server-status");
    if (!res.ok) return true;
    const info = await res.json();
    return info.lobbyCreationEnabled !== false;
  } catch {
    return true;
  }
}

function goToLobby(code: string, onReady: (code: string) => void): void {
  history.pushState({}, "", `/lobby/${code}`);
  onReady(code);
}

interface PickerState {
  lobbyCreationEnabled: boolean;
  errorMessage?: string;
}

/** Renders the picker immediately with creation optimistically enabled (so
 * the screen never waits on a network round trip to paint), then re-renders
 * once the real status is known - in the ordinary case creation IS enabled,
 * so this is imperceptible; in the rare "closed for an update" case the
 * banner pops in a moment later instead of blocking first paint. */
function renderPickerWithLiveStatus(container: HTMLElement, onReady: (code: string) => void, errorMessage?: string): void {
  renderPicker(container, onReady, { lobbyCreationEnabled: true, errorMessage });
  fetchLobbyCreationEnabled().then((enabled) => {
    if (!enabled) renderPicker(container, onReady, { lobbyCreationEnabled: false, errorMessage });
  });
}

function renderPicker(container: HTMLElement, onReady: (code: string) => void, state: PickerState): void {
  container.innerHTML = "";

  if (!state.lobbyCreationEnabled) {
    const banner = document.createElement("div");
    banner.className = "lobby-creation-disabled-banner";
    banner.textContent =
      "An update is queued and Lobby creation is currently disabled. Please try again in 30-60 minutes.";
    container.appendChild(banner);
  }

  const wrap = document.createElement("div");
  wrap.className = "lobby-select-wrap";

  const heading = document.createElement("div");
  heading.className = "lobby-select-heading";

  const title = document.createElement("h1");
  title.appendChild(buildLogoSvg("logo logo--hero"));
  heading.appendChild(title);

  const tagline = document.createElement("p");
  tagline.className = "lobby-select-tagline";
  tagline.textContent = "A free online drawing and guessing game, with a couple twists.";
  heading.appendChild(tagline);

  wrap.appendChild(heading);

  if (state.errorMessage) {
    const err = document.createElement("p");
    err.className = "form-error";
    err.textContent = state.errorMessage;
    wrap.appendChild(err);
  }

  const createBtn = document.createElement("button");
  createBtn.type = "button";
  createBtn.className = "primary-button";
  createBtn.textContent = "Create a New Lobby";
  createBtn.disabled = !state.lobbyCreationEnabled;
  createBtn.addEventListener("click", () => {
    createBtn.disabled = true;
    fetch("/api/lobbies", { method: "POST" })
      .then((res) => {
        if (!res.ok) {
          return res
            .json()
            .catch(() => null)
            .then((body) => Promise.reject(body?.error === "Lobby creation is currently disabled." ? "disabled" : "other"));
        }
        return res.json() as Promise<LobbyInfoDTO>;
      })
      .then((info) => goToLobby(info.code, onReady))
      .catch((reason) => {
        if (reason === "disabled") {
          renderPicker(container, onReady, { lobbyCreationEnabled: false });
        } else {
          renderPickerWithLiveStatus(container, onReady, "Could not create a lobby. Please try again.");
        }
      });
  });
  wrap.appendChild(createBtn);

  const divider = document.createElement("p");
  divider.className = "hint-text";
  divider.textContent = "or join an existing one";
  wrap.appendChild(divider);

  const form = document.createElement("form");
  form.className = "join-form";
  const label = document.createElement("label");
  label.className = "field-label";
  label.textContent = "Lobby code";
  const input = document.createElement("input");
  input.type = "text";
  input.maxLength = 8;
  input.autocomplete = "off";
  input.placeholder = "e.g. 5D04X33E";
  input.className = "lobby-code-input";
  const submitBtn = document.createElement("button");
  submitBtn.type = "submit";
  submitBtn.className = "secondary-button";
  submitBtn.textContent = "Join Lobby";
  form.append(label, input, submitBtn);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const code = input.value.trim().toUpperCase();
    if (!CODE_PATTERN.test(code)) {
      renderPickerWithLiveStatus(container, onReady, "That doesn't look like a valid lobby code.");
      return;
    }
    submitBtn.disabled = true;
    fetchLobbyInfo(code)
      .then((info) => {
        if (!info.exists) {
          renderPickerWithLiveStatus(container, onReady, `Lobby "${code}" wasn't found - it may have expired.`);
          return;
        }
        goToLobby(code, onReady);
      })
      .catch(() => renderPickerWithLiveStatus(container, onReady, "Could not reach the server. Please try again."));
  });
  wrap.appendChild(form);

  container.appendChild(wrap);
}

/** Entry point: if the URL already names a lobby, validate it before connecting
 * (a friendly "not found" beats a raw socket failure) and skip straight to
 * `onReady`; otherwise render the create/join picker. */
export function initLobbySelect(container: HTMLElement, onReady: (code: string) => void): void {
  const codeFromUrl = parseCodeFromPath(window.location.pathname);
  if (!codeFromUrl) {
    renderPickerWithLiveStatus(container, onReady);
    return;
  }
  fetchLobbyInfo(codeFromUrl)
    .then((info) => {
      if (info.exists) {
        onReady(codeFromUrl);
      } else {
        history.pushState({}, "", "/");
        renderPickerWithLiveStatus(container, onReady, `Lobby "${codeFromUrl}" wasn't found - it may have expired.`);
      }
    })
    .catch(() => renderPickerWithLiveStatus(container, onReady, "Could not reach the server. Please try again."));
}
